/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/jsx-filename-extension */
/* eslint-disable no-shadow */
/* eslint-disable no-use-before-define */
import React, { FC, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Separator, mergeStyles, IStyle, PrimaryButton, DefaultButton, IGroup } from '@fluentui/react';
import { ColumnChooser } from './src/index';
import { ExclusionRule, CategoryDescriptor, CandidateItem, SelectedItem, ItemState, ItemKind, isLockedCandidateItem } from './src/types';
import { intersection } from './src/utils/exclusionRuleHelper';
import { theme } from './theme';
import { edgeDistance } from './src/utils/style';
import { convertColumnsToReportAggregation, filterCategories, SelectOption, Option, CategoryDataModel, } from './helper';

// TODO: Something should be done in the future:
// 1. log
// 2. for selected columns, use Up/Down/Space to move

const columnChooserStyle: IStyle = {
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Segoe UI',
  height: '100%',
  '.reports-v4-column-chooser-header': {
    marginTop: edgeDistance,
    marginLeft: edgeDistance,
    fontWeight: 'bold',
    fontSize: 18,
    lineHeight: 24,
  },
  '.reports-v4-column-chooser-tip': {
    marginLeft: edgeDistance,
    color: 'grey',
  },
  '.reports-v4-column-chooser': {
    flexGrow: 1,
    // ridiculous, but works(ocupy all remaining space of its parent elemnt,
    // no matter how high its children are), 8000 is a big enough value.
    height: 'calc(100% - 8000px)',
    '.column-lists': {
      height: '100%',
    },
    '.list-wrapper': {
      borderWidth: 1,
      borderColor: 'black',
      borderStyle: 'solid',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      '.list-header': {
        padding: 5,
        fontWeight: 'bold',
      },
      '.action-group': {
        marginTop: 5,
        marginLeft: 5,
        marginBottom: 5,
      },
      '.ms-SearchBox': {
        marginLeft: 5,
        marginRight: 5,
      },
      '.wrapped-group-header': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        ':hover': {
          background: 'rgb(243, 242, 241)',
          '.group-operation': {
            display: 'inline',
          },
        },
        '.ms-GroupHeader': {
          overflow: 'hidden',
        },
        '.group-operation': {
          display: 'none',
          color: 'rgb(0, 120, 212)',
          cursor: 'pointer',
          paddingRight: 8,
          fontSize: 14,
          flexShrink: 0,
          ':hover': {
            color: 'rgb(0, 69, 120)',
          },
        },
      },
      '.ms-GroupHeader-expand': {
        flexShrink: 0,
      },
      '.ms-GroupHeader-title': {
        overflow: 'hidden',
      },
    },
    '.categories-wrapper': {
      flexGrow: 1,
      // ridiculous, but works(ocupy all remaining space of its parent elemnt,
      // no matter how high its children are), 8000 is a big enough value.
      height: 'calc(100% - 8000px)',
      display: 'flex',
      flexDirection: 'column',
      '[role=tabpanel]': {
        flexGrow: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
      },
    },
    '.selected-items-list': {
      flexGrow: 1,
      // ridiculous, but works(ocupy all remaining space of its parent elemnt,
      // no matter how high its children are), 8000 is a big enough value.
      height: 'calc(100% - 8000px)',
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    '.item-row': {
      paddingRight: 8,
      // reference https://stackoverflow.com/questions/8468066/child-inside-parent-with-min-height-100-not-inheriting-height
      minHeight: 'inherit',
      display: 'flex',
      fontSize: 14,
      '.text-cell': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '.cell': {
        paddingTop: 9,
        paddingLeft: 10,
      },
      '.operation-cell': {
        marginLeft: 'auto',
      },
      ':hover, :focus, :focus-within': {
        backgroundColor: theme.hoverBackgroundColor,
        '.iconba-GripperBarHorizontal': {
          ':after': {
            color: 'black',
            content: '"\\F090\\F08E"',
            position: 'absolute',
            fontSize: '16px',
            marginTop: '-6px',
            letterSpacing: '1px',
            marginLeft: '-18px',
            writingMode: 'vertical-lr',
          },
        },
        '.operation': {
          display: 'flex',
        },
      },
      '.icon-container': {
        margin: 9,
        width: 20,
        height: 20,
        flexShrink: 0,
        '.iconba': {
          top: 0,
          color: '#0078D4',
          fontSize: 20,
        },
      },
    },
    '.item-row.dragging': {
      opacity: 0.5,
    },
    '.item-list': {
      '.operation': {
        display: 'none',
      },
    },
    '.operation.disabled': {
      cursor: 'not-allowed',
    },
    '.operation.enabled': {
      color: theme.enableTextColor,
      cursor: 'pointer',
      ':hover, :focus': {
        color: theme.enableTextHoverColor,
      },
    },
  },
  '.reports-v4-column-chooser-error-messages': {
    marginLeft: edgeDistance,
    marginRight: edgeDistance,
    color: '#A80000',
  },
  '.button-group': {
    marginLeft: edgeDistance,
    marginBottom: edgeDistance,
    '.cancel-button': {
      marginLeft: edgeDistance / 2,
    },
  },
};

const columnChooserStyleClassName = mergeStyles(columnChooserStyle);

export function extractExclusionRulesFromExclusionSets(exclusionSets: any): ExclusionRule[] {
  const rules: ExclusionRule[] = [];
  // TODO: should we check whether they are always paris? what should we do if there is not pair?
  const dimensionExclusionSetKeys = Object.keys(exclusionSets).filter(key => key.startsWith('Dimension'));
  dimensionExclusionSetKeys.forEach((key) => {
    const correspondKey = getCorrespondKey(key);
    const set = exclusionSets[key];
    const correspondSet = exclusionSets[correspondKey];
    const exclusionRule: ExclusionRule = {
      keys: new Set(set),
      predicate: ({ selectedKeys }) => {
        const triggerColumnKeys = intersection(correspondSet, selectedKeys);
        return [triggerColumnKeys.length !== 0, { triggerColumnKeys }];
      },
    };
    const correspondExclusionRule: ExclusionRule = {
      keys: new Set(correspondSet),
      predicate: ({ selectedKeys }) => {
        const triggerColumnKeys = intersection(set, selectedKeys);
        return [triggerColumnKeys.length !== 0, { triggerColumnKeys }];
      },
    };
    rules.push(exclusionRule, correspondExclusionRule);
  });

  return rules;

  function getCorrespondKey(key: string) {
    if (key.startsWith('Dimension')) {
      return key.replace('Dimension', 'Metric');
    } else if (key.startsWith('Metric')) {
      return key.replace('Metric', 'Dimension');
    }
    throw new Error('unknwon exclusion set name format');
  }
}

interface AggregationExclusion {
  [reportAggregation: string]: string[];
}

/**
 * why a column is excluded
 *
 * key is the "locked" column, value is the columns that make it "locked".
 */
type ConlicktColumnMap = Map<string, string[]>;

export function executeExcludeRules(selectedKeys: Set<string>, rules: ExclusionRule[]) {
  const res = new Set<string>();
  const conflictColumnMap: ConlicktColumnMap = new Map<string, string[]>();
  rules.forEach((rule) => {
    const [predicateValue, context] = rule.predicate({ selectedKeys });
    if (predicateValue) {
      rule.keys.forEach((key) => {
        res.add(key);

        if (!conflictColumnMap.has(key)) {
          conflictColumnMap.set(key, []);
        }

        const conflictColumns = conflictColumnMap.get(key);
        context.triggerColumnKeys.forEach((c) => {
          if (!conflictColumns?.includes(c)) {
            conflictColumns?.push(c);
          }
        });
      });
    }
  });
  return conflictColumnMap;
}

export function getMandatoryItemKeys(categories: CategoryDataModel[]) {
  const res: string[] = [];
  categories.forEach(category =>
    category.options.forEach((option) => {
      if (option.selectOption === SelectOption.mandatory) {
        res.push(option.key);
      }
    }));
  return res;
}

/**
 * Note that the parameter is not clean, even insane
 * We choose to pass the raw data as small as possiable , rather than exactly what the component need
 * for the requirement is changing frequently
 * This should be improved in the future if possiable.
 */
interface ReportsV4ColumnChooserProps {
  categories: CategoryDataModel[];
  exclusionSets: any;
  AggregationExclusion: AggregationExclusion | undefined;
  selectedKeys: string[];
  MandateColumnSet: any;
  MissingMandatoryColumnError: any;
  onSaveClick: (selectedKeys: string[]) => void;
  onCancelClick: (selectedKeys: string[]) => void;
  isTimeColumnMandatory: boolean;
  // onSelectedKeysChange?: (selectedKeys: string[]) => void;
}

export function buildCandidateItem(
  option: Option,
  conflictColumnMap: ConlicktColumnMap,
  selectedKeysSet: Set<string>,
  i18n: any,
  keyNameMap: Record<string, string>
): CandidateItem {
  const candidateItem: CandidateItem = {
    key: option.key,
    friendlyName: option.name,
    helpTopic: option.helpTopic,
    state: getItemStateOfOption(option, conflictColumnMap),
    kind: ItemKind.candidate,
    isSelected: selectedKeysSet.has(option.key),
  };

  if (isLockedCandidateItem(candidateItem)) {
    const conflictColumnsFriendlyName = conflictColumnMap.get(option.key)!.map(c => keyNameMap[c]);
    candidateItem.lockReason = getLockReasonElement(option.name, conflictColumnsFriendlyName);
  }

  return candidateItem;

  function getItemStateOfOption(option: Option, conflictColumnMap: ConlicktColumnMap): ItemState {
    switch (true) {
      case option.selectOption === SelectOption.mandatory:
        return ItemState.mandatory;
      case conflictColumnMap.has(option.key):
        return ItemState.lock;
      default:
        return ItemState.optional;
    }
  }

  function getLockReasonElement(lockColumnFriendlyName: string, conflictColumnsFriendlyName: string[]) {
    const oneColumnTemplate = _TL_('This field is not compatible with [{{firstColumnName}}]');
    const twoColumnTemplate = _TL_('This field is not compatible with [{{firstColumnName}}] and [{{secondColumnName}}]');
    const threeAndMoreColumnTemplate = _TL_('This field is not compatible with [{{firstColumnName}}], [{{secondColumnName}}] and {{remainCount}} more filed(s).');
    const message = (() => {
      const { length } = conflictColumnsFriendlyName;
      // length must larger than 0
      if (length <= 0) { throw new Error('length must be positive'); }
      if (length === 1) {
        return i18n.getString(oneColumnTemplate, { firstColumnName: conflictColumnsFriendlyName[0] });
      } else if (length === 2) {
        return i18n.getString(twoColumnTemplate, {
          firstColumnName: conflictColumnsFriendlyName[0],
          secondColumnName: conflictColumnsFriendlyName[1],
        });
      }
      return i18n.getString(threeAndMoreColumnTemplate, {
        firstColumnName: conflictColumnsFriendlyName[0],
        secondColumnName: conflictColumnsFriendlyName[1],
        remainCount: (length - 2).toString(),
      });
    })();

    return (
      <div className="tooltip-reason-wrapper">
        <div style={{ marginBottom: 5 }} className="lock-column-name">
          {lockColumnFriendlyName}
        </div>
        <div className="mutex-column-message">
          {message}
        </div>
      </div>
    );
  }
}

export function extractKeyNameObejct(categories: CategoryDataModel[]) {
  const res: Record<string, string> = {};
  categories.forEach((category) => {
    category.options.forEach((option) => {
      res[option.key] = option.name;
    });
  });
  return res;
}

const TimeColumnsCategoryKey = 'TimeColumns';
const DimensionColumnsCategoryKey = 'DimensionColumns';
const MetricColumnsCategoryKey = 'MetricColumns';

const atLeastOneCategory = new Set([
  DimensionColumnsCategoryKey,
  MetricColumnsCategoryKey,
]);

const atLeastOneErrorMessageMap = {
  [TimeColumnsCategoryKey]: _TL_('At least one Time column must be selected.'),
  [DimensionColumnsCategoryKey]: _TL_('At least one Attribute column must be selected.'),
  [MetricColumnsCategoryKey]: _TL_('At least one Performance Statistics column must be selected.'),
};

function checkValid({
  MandateColumnSet = [], isTimeColumnMandatory,
  MissingMandatoryColumnError, categories, selected,
}: {
    categories: CategoryDataModel[],
    isTimeColumnMandatory: boolean,
    i18n: any,
    [foo: string]: any,
  }) {
  const selectedSet = new Set(selected);

  const errorMessages: string[] = [];

  categories.forEach((category) => {
    const isCurrentCategoryMustBeSelectedOneColumn = atLeastOneCategory.has(category.type)
      || (category.type === TimeColumnsCategoryKey && isTimeColumnMandatory);
    if (isCurrentCategoryMustBeSelectedOneColumn) {
      if (!category.options.some(option => selectedSet.has(option.key))) {
        errorMessages.push(atLeastOneErrorMessageMap[category.type]);
      }
    }
  });

  let isMandatoryColumnMissing = false;
  MandateColumnSet.forEach((mandateColumns) => {
    if (!mandateColumns.some(column => selectedSet.has(column))) {
      isMandatoryColumnMissing = true;
    }
  });

  if (isMandatoryColumnMissing) {
    errorMessages.push(MissingMandatoryColumnError);
  }

  return errorMessages;
}

export function buildSelectedItem(selectedItemKey: string, mandatoryKeys: string[], keyNameMap: Record<string, string>): SelectedItem {
  return {
    key: selectedItemKey,
    friendlyName: keyNameMap[selectedItemKey],
    state: mandatoryKeys.includes(selectedItemKey) ? ItemState.mandatory : ItemState.optional,
    kind: ItemKind.selected,
  };
}

// mandatoryKeys is usually pretty small, no need to convert to `Set`
export function buildSelectedItems(selectedItemKeys: string[], mandatoryKeys: string[], keyNameMap: Record<string, string>): SelectedItem[] {
  return selectedItemKeys.map(selectedItemKey => buildSelectedItem(selectedItemKey, mandatoryKeys, keyNameMap));
}

export const ReportsV4ColumnChooser: FC<ReportsV4ColumnChooserProps> = (props) => {
  const {
    exclusionSets, categories, selectedKeys, onCancelClick, onSaveClick,
    MandateColumnSet, MissingMandatoryColumnError, isTimeColumnMandatory,
    AggregationExclusion = {},
  } = props;

  const [, forceRefresh] = useState(0);
  const i18n: any = useContext(I18nContext);
  const headerNameText: string = i18n.getString(_TL_('Modify columns'));
  const tipText: string = i18n.getString(_TL_('Select Add to customize the report columns. Then, drag and drop the columns to rearrange them into the order you want to view them in. You can also modify columns using the arrow keys.'));

  const translatedCategories: ReportsV4ColumnChooserProps['categories'] = useMemo(() => categories.map(category => ({
    ...category,
    name: i18n.getString(category.name),
    options: category.options.map(option => ({
      ...option,
      name: i18n.getString(option.name),
    })),
  })), [categories, i18n]);

  const mandatoryKeys = useMemo(() => getMandatoryItemKeys(translatedCategories), [translatedCategories]);

  const [currentSelectedKeys, setCurrentSelectedKeys] = useState(selectedKeys);
  useEffect(() => {
    setCurrentSelectedKeys(selectedKeys);
  }, [selectedKeys]);

  // for performance, convert the array to set
  const selectedKeysSet = new Set(currentSelectedKeys);

  // Seems this is pretty useless, `exclusionRule` should be much more better if it is a parameter.
  const exclusionRules = useMemo(() => [
    ...extractExclusionRulesFromExclusionSets(exclusionSets),
  ], [exclusionSets]);

  // this is so weird, ideally, we want to import i18nMap from "@bingads-webui-campaign/report-shared", but it need jquery, why?
  const keyNameMap = useMemo(() => extractKeyNameObejct(translatedCategories), [translatedCategories]);

  const conflictColumnMap = executeExcludeRules(selectedKeysSet, exclusionRules);

  const selectedItems = buildSelectedItems(currentSelectedKeys, mandatoryKeys, keyNameMap);

  const hiddenCandidateColumnKeys = new Set(AggregationExclusion[convertColumnsToReportAggregation(selectedKeysSet)]);

  const equivalentReportAggregation = convertColumnsToReportAggregation(selectedKeysSet);
  const categoriesfilteredCategories = filterCategories(translatedCategories, { timeGrain: equivalentReportAggregation });

  const categoryDescriptorOfStatic = fromDataModel(categoriesfilteredCategories, conflictColumnMap, selectedKeysSet, hiddenCandidateColumnKeys);
  const lastcategoryDescriptorOfStatic = useRef<CategoryDescriptor>();
  mergeWithLastTimeData(categoryDescriptorOfStatic, lastcategoryDescriptorOfStatic.current);

  useEffect(() => {
    lastcategoryDescriptorOfStatic.current = categoryDescriptorOfStatic;
  });

  function mergeWithLastTimeData(category: CategoryDescriptor, lastCategory: CategoryDescriptor | undefined) {
    if (category.key === lastCategory?.key) {
      const groupLength = category.groups.length;
      for (let index = 0; index < groupLength; index++) {
        // eslint-disable-next-line no-param-reassign
        category.groups[index].isCollapsed = lastCategory.groups[index].isCollapsed;
      }
    }
  }

  function fromDataModel(
    categories: CategoryDataModel[],
    conflictColumnMap: ConlicktColumnMap,
    selectedKeySet: Set<string>,
    hiddenCandidateColumnKeys: Set<string>
  ): CategoryDescriptor {
    return {
      headerName: i18n.getString(_TL_('All')),
      key: 'All',
      groups: categories.map(group => ({
        key: group.type,
        name: group.name,
        items: group.options.map(option =>
          buildCandidateItem(option, conflictColumnMap, selectedKeySet, i18n, keyNameMap))
          .filter(item => !hiddenCandidateColumnKeys.has(item.key)),
        isCollapsed: false,
      })),
    };
  }

  function onCollapseAllClick() {
    if (lastcategoryDescriptorOfStatic.current) {
      lastcategoryDescriptorOfStatic.current.groups.forEach((group) => {
        // eslint-disable-next-line no-param-reassign
        group.isCollapsed = true;
      });
    }
    forceRefresh(1);
  }

  function onExpandAllClick() {
    if (lastcategoryDescriptorOfStatic.current) {
      lastcategoryDescriptorOfStatic.current.groups.forEach((group) => {
        // eslint-disable-next-line no-param-reassign
        group.isCollapsed = false;
      });
    }
    forceRefresh(2);
  }

  function onToggleCollapse(triggerGroup: IGroup) {
    if (lastcategoryDescriptorOfStatic.current) {
      lastcategoryDescriptorOfStatic.current.groups.forEach((group) => {
        if (triggerGroup.key === group.key) {
          // eslint-disable-next-line no-param-reassign
          group.isCollapsed = !triggerGroup.isCollapsed;
        }
      });
    }
    forceRefresh((prev) => {
      // always force to update
      let next = 0;
      while (true) {
        // 1 - 10 is reserved.
        if (next > 10 && next !== prev) break;
        next = Math.floor(Math.random() * 100);
      }
      return next;
    });
  }

  const errorMessages = checkValid({
    categories: translatedCategories,
    i18n,
    MandateColumnSet,
    isTimeColumnMandatory,
    MissingMandatoryColumnError,
    selected: currentSelectedKeys,
  });

  return (
    <div className={`${columnChooserStyleClassName} reports-v4-column-chooser-wrapper`}>
      <div className="reports-v4-column-chooser-header">{headerNameText}</div>
      <div className="reports-v4-column-chooser-tip">{tipText}</div>
      <ColumnChooser
        className="reports-v4-column-chooser"
        onCollapseAllClick={onCollapseAllClick}
        onExpandAllClick={onExpandAllClick}
        onToggleCollapse={onToggleCollapse}
        categories={[categoryDescriptorOfStatic]}
        onSelectedKeysChange={(newKeys) => {
          const equivalentReportAggregation = convertColumnsToReportAggregation(new Set(newKeys));
          const hiddenCandidateColumns = new Set(AggregationExclusion[equivalentReportAggregation] ?? []);
          // eslint-disable-next-line no-param-reassign
          newKeys = newKeys.filter(key => !hiddenCandidateColumns.has(key));
          setCurrentSelectedKeys(newKeys);
        }}
        onAddItems={(items) => {
          // Note: items might includes selected columns, for example, it includes all columns when click "Add All"
          let selectedKeys = selectedItems.map(item => item.key);
          let lastSelectedKeys;
          let loopCount = 0;
          // After selecting some time column in reports-v4 column chooser, some selected columns will be removed. Then some "locked" columns now will
          // be unlocked. So we need a loop to try to get a stable state.
          do {
            loopCount++;
            if (loopCount > 50) {
              throw new Error('We might trap into infinate loop, please check reports-column-chooser.');
            }
            lastSelectedKeys = [...selectedKeys];
            // eslint-disable-next-line no-loop-func
            items.forEach((item) => {
              const newSet = new Set(selectedKeys);
              if (newSet.has(item.key)) {
                return;
              }
              newSet.add(item.key);
              const conflictColumnMap = executeExcludeRules(newSet, exclusionRules);
              const equivalentReportAggregation = convertColumnsToReportAggregation(newSet);
              const hiddenCandidateColumns = new Set(AggregationExclusion[equivalentReportAggregation] ?? []);
              if (!conflictColumnMap.has(item.key)) {
                selectedKeys = [...selectedKeys, item.key].filter(key => !hiddenCandidateColumns.has(key));
              }
            });
          }
          while (selectedKeys.length !== lastSelectedKeys.length);

          setCurrentSelectedKeys(selectedKeys);
        }}
        selectedItems={selectedItems}
      />
      <div className="reports-v4-column-chooser-error-messages">
        {
          errorMessages.map(errorMessage =>
            (<div>{i18n.getString(errorMessage)}</div>))
        }
      </div>
      <Separator />
      <div className="button-group">
        <PrimaryButton className="apply-button" disabled={errorMessages.length !== 0} onClick={() => { onSaveClick?.(currentSelectedKeys); }} text={i18n.getString(_TL_('Save'))} />
        <DefaultButton className="cancel-button" onClick={() => { onCancelClick?.(currentSelectedKeys); }} text={i18n.getString(_TL_('Cancel'))} />
      </div>
    </div>
  );
};

export * from './helper';
